import { useEffect, useState, forwardRef, useImperativeHandle } from 'react'
import { Button } from '@/components/ui/button'
import { LLMChoice } from '@/redux/types/room'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Edit } from 'lucide-react'
import { toast } from 'sonner'
import { saveAdvisorData } from '@/api/configuration'
import { useNavigate } from 'react-router-dom'

interface LlmPreferenceProps {
  chatbotId: string
  chatbotLLM: LLMChoice
  disabled: boolean
}

export interface LlmPreferenceRef {
  getSelectedLLM: () => LLMChoice
}

const LlmPreference = forwardRef<LlmPreferenceRef, LlmPreferenceProps>(
  ({ chatbotId, chatbotLLM, disabled }, ref) => {
    const [selectedLLM, setSelectedLLM] = useState<LLMChoice>(
      chatbotLLM || LLMChoice.CLAUDE
    )
    const [isLoading, setIsLoading] = useState(false)
    const navigate = useNavigate()

    useEffect(() => {
      if (chatbotLLM) {
        setSelectedLLM(chatbotLLM)
      }
    }, [chatbotLLM])

    const hasChanged = selectedLLM !== chatbotLLM

    useImperativeHandle(ref, () => ({
      getSelectedLLM: () => selectedLLM,
    }))

    const handleSubmit = async () => {
      if (!chatbotId) {
        toast.error('Chatbot ID is missing')
        return
      }

      if (!selectedLLM) {
        toast.error('Please select an LLM model')
        return
      }

      setIsLoading(true)

      try {
        await saveAdvisorData(chatbotId, {
          chatbot_llm: selectedLLM,
        })

        toast.success('LLM configuration updated successfully!')
        navigate(0)
      } catch (error) {
        console.error('Failed to update LLM configuration:', error)

        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to update LLM configuration. Please try again.'

        toast.error(errorMessage)
      } finally {
        setIsLoading(false)
      }
    }

    return (
      <div className='space-y-6'>
        <h2 className='text-2xl font-semibold text-foreground'>
          Preferred LLM Model
        </h2>
        <div className='space-y-4 mt-6 p-4 border border-blue-200 bg-blue-50 rounded-lg'>
          <div className='space-y-2'>
            <label className='text-sm font-medium text-foreground'>
              Select LLM Model
            </label>
            <Select
              value={selectedLLM}
              onValueChange={(value) => {
                setSelectedLLM(value as LLMChoice)
              }}
              disabled={isLoading || disabled}
            >
              <SelectTrigger className='h-10 bg-white text-sm w-[300px]'>
                <SelectValue placeholder='Select preferred LLM model' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={LLMChoice.OPENAI}>OpenAI</SelectItem>
                <SelectItem value={LLMChoice.CLAUDE}>Claude</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button
            onClick={handleSubmit}
            disabled={!chatbotId || !selectedLLM || isLoading || !hasChanged}
            className='bg-green-600 hover:bg-green-700 disabled:opacity-50'
          >
            <Edit className='w-4 h-4 mr-2' />
            {isLoading ? 'Updating...' : 'Update LLM Preference'}
          </Button>
        </div>
      </div>
    )
  }
)

LlmPreference.displayName = 'LlmPreference'

export default LlmPreference
